import React, {useState, useCallback, useRef, useMemo, useEffect} from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import {makeStyles} from '@mui/styles'
import {useSnackbar} from 'notistack';
import Collapse from '@mui/material/Collapse';
import Paper from '@mui/material/Paper';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {useToggle} from '../../../utils/hooks';
import {useTranslation} from 'react-i18next';
import SMCLabel from '../../../components/Simple/SMCLabel';
import Api from '../../../api/SMPFetchHelper';
import {enqueueExcelDownload, getAxiosFilename, getErrorCode} from '../../../utils/utils';
import SMCButton from '../../../components/Simple/SMCButton';
import downloadIcon from '../../../assets/images/download.svg';
import loadingIcon from '../../../assets/images/loading_spinner.gif';
import RowLayout from '../../../components/Layouts/RowLayout';

const useStyles = makeStyles(theme => ({
    card: {
        maxWidth: 400,
        minWidth: 344,
    },
    header: {
        color: '#FFF',
        fontWeight: 'bold',
        fontSize: '0.875rem',
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        lineHeight: '1.57',
        letterSpacing: '0.00714em'
    },
    actionRoot: {
        padding: '8px 8px 8px 16px',
        backgroundColor: '#2F3F57',
    },
    icons: {
        marginLeft: 'auto !important'
    },
    expand: {
        padding: '8px 8px',
        transform: 'rotate(0deg)',
        transition: theme.transitions.create('transform', {
            duration: theme.transitions.duration.shortest,
        }),
    },
    expandOpen: {
        transform: 'rotate(180deg)',
    },
    collapse: {
        padding: 16,
    },
    checkIcon: {
        fontSize: 20,
        color: '#b3b3b3',
        paddingRight: 4,
    },
    button: {
        padding: 0,
        textTransform: 'none',
    },
}));

const GenerateSystemReportSnackbarContent = React.forwardRef(({
    message,
    isGlobal,
    onClose,
    id
}, ref) => {
    const { t } = useTranslation();
    const { closeSnackbar } = useSnackbar();
    const classes = useStyles();

    const [inProgress, setInProgress] = useState(true);
    const [hasError, setHasError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [excelData, setExcelData] = useState(null);
    const [excelName, setExcelName] = useState(null);

    const canceled = useRef(false);
    // Keep a reference to the last fetch promise, so we can ignore all old responses
    const lastFetchPromise = useRef();

    const [expanded, toggleExpanded] = useToggle(false);

    const handleExpandClick = useCallback(() => {
        toggleExpanded();
    }, [toggleExpanded]);

    const handleDismiss = useCallback(() => {
        canceled.current = true;
        onClose && onClose();
        closeSnackbar(id);
    }, [closeSnackbar, onClose, id]);

    const startExport = useCallback(async () => {
        let currentPromise;
        try {
            setInProgress(true);
            if (isGlobal) {
                currentPromise = Api.generateSystemReports();
            }
            else {
                currentPromise = Api.generateDomainReports();
            }
            // Save as last promise
            lastFetchPromise.current = currentPromise;
            const resp = await currentPromise;
            const fileName = getAxiosFilename(resp) || 'excel.xlsx';
            // Only set the state if this is the newest fetch command
            if (lastFetchPromise.current === currentPromise) {
                setHasError(false);
                setExcelData(resp.data);
                setExcelName(fileName);
            }
        } catch (e) {
            // Only set the state if this is the newest fetch command
            if (lastFetchPromise.current === currentPromise) {
                setHasError(true);
                const errorCode = getErrorCode(e) || 'GENERAL_ERROR';
                setErrorMessage(t(errorCode));
            }
        }
        setInProgress(false);
    }, [t, isGlobal]);

    useEffect(() => {
        startExport();
    }, [startExport]);

    const headerText = useMemo(() => {
        if (hasError) {
            return 'Error';
        }
        else if (inProgress) {
            return 'Creating excel file...';
        }
        else {
            return 'Finished creating Excel file';
        }
    }, [hasError, inProgress]);

    const body = useMemo(() => {
        if (hasError) {
            return <SMCLabel>{errorMessage}. Please try again</SMCLabel>
        }
        else if (inProgress) {
            return (
                <RowLayout><img src={loadingIcon} alt="" height="24px" width="26px" /><SMCLabel fontSize="15px">This might take a few minutes...</SMCLabel></RowLayout>
            )
        }
        else if (excelData && excelName) {
            return (
                <SMCButton tooltip={t('SMC_GEN_DOWNLOAD')} variant="ghost" onClick={() => enqueueExcelDownload(excelData, excelName)}>
                    <RowLayout spacing="4px">
                        <img src={downloadIcon} alt="download" width="16px" height="16px" />
                        <SMCLabel bold inline>Click to download</SMCLabel>
                    </RowLayout>
                </SMCButton>
            );
        }
        else {
            return null;
        }
    }, [hasError, errorMessage, inProgress, excelData, excelName, t]);
    
    return (
        <Card className={classes.card} ref={ref}>
            <CardActions classes={{ root: classes.actionRoot }}>
                <SMCLabel className={classes.header}>{headerText}</SMCLabel>
                <div className={classes.icons}>
                    <IconButton
                        aria-label="Show more"
                        className={classnames(classes.expand, { [classes.expandOpen]: expanded })}
                        onClick={handleExpandClick}
                    >
                        <ExpandMoreIcon style={{ color: 'white' }} />
                    </IconButton>
                    <IconButton className={classes.expand} onClick={handleDismiss}>
                        <CloseIcon style={{ color: 'white' }} />
                    </IconButton>
                </div>
            </CardActions>
            <Collapse in={expanded} timeout="auto" unmountOnExit>
                <Paper className={classes.collapse}>
                    {body}
                    {/* {buttons} */}
                </Paper>
            </Collapse>
        </Card>
    );
});

GenerateSystemReportSnackbarContent.propTypes = {
    id: PropTypes.number.isRequired,
};

export default GenerateSystemReportSnackbarContent;